import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { WaveformLinesIcon } from '../theme/Icons';
import { IRecording, RecordingState } from './RecordingState';
import { initCap } from '../utils/format';
import { RevenueCatModel } from '../innerhive-plus/revenue-cat/RevenueCatModel';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { InnerhivePlusText } from '../components/InnerhivePlusText';
import { InnerhivePlusDialogViewModel } from '../innerhive-plus/InnerhivePlusDialogViewModel';
import { IntercomService } from '../support/IntercomService';
import { RecordingRetryButton } from './RecordingRetryButton';
import dayjs from 'dayjs';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';

const useStyles = createUseStyles({
    recordingInfo: {
        gap: 8,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        fontSize: 14,
        paddingLeft: 4,
        color: 'var(--ion-color-step-600, #666666)'
    },
    uploadFailedContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: 'var(--ion-color-danger)'
    }
});

const ContactUsLink: React.FC = () => {
    const intercomService = useInstance(IntercomService);
    const onClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        intercomService.displayMessenger();
    };
    return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a onClick={onClick} style={{cursor: 'pointer'}}>(Contact Us)</a>
    );
};

const ProcessingFailed: FC = () => {
    return <><span style={{color: 'var(--ion-color-danger)'}}>Processing Failed</span> <ContactUsLink /></>;
};

export type IRecordingInfoProps = {
    recording?: Partial<IRecording>;
    hideStatus?: boolean;
};

export const RecordingInfo: FC<IRecordingInfoProps> = ({ recording, hideStatus }) => {
    const classes = useStyles();

    const principal = useInstance(AppPrincipal);
    const isAnonymous = isAnonymousViewer(principal);

    const innerhivePlusDialogVM = useInstance(InnerhivePlusDialogViewModel);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    if (!recording) {
        return null;
    }

    const formatRecordingTime = (milliseconds: number | undefined): string => {
        if (!milliseconds) {
            return '';
        }

        const totalSeconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        // const formattedMinutes = String(minutes).padStart(2, '0');
        // const formattedSeconds = String(seconds).padStart(2, '0');

        //return `${formattedMinutes} min ${formattedSeconds} sec`;
        //return `${formattedMinutes}:${formattedSeconds}`;
        return `${minutes < 1 ? '' : minutes+'m ' }${seconds}s`;
    };

    const getRecordingStatus = () => {
        const hoursInStatus = () => {
            return dayjs().diff(dayjs(recording.created), 'hours');
        };

        const maxHours = 1;
        const hours = hoursInStatus();
        switch(recording.status) {
            case 'recording-complete':
                return 'Recording Complete';
            case 'uploading':
                return hours >= maxHours ? <ProcessingFailed /> : 'Uploading...';
            case 'upload-failed':
                return (
                    <div className={classes.uploadFailedContainer}>
                        <span>Upload Failed</span>
                        {recording.id && (
                            <RecordingRetryButton
                                size="small"
                                recordingId={recording.id}
                            />
                        )}
                    </div>
                );
            case 'uploaded':
            case 'transcribed':
                const innerhivePlusMsg = !isAnonymous && !revenueCat.hasInnerhivePlus() && recordingState.isRecordingAILimitReached() && recording.skipAIProcessing ? (
                    <div
                        data-type="innerhive-plus-msg"
                        style={{display: 'flex', alignItems: 'center'}}
                        onClick={async (e: React.MouseEvent) => {
                            e.stopPropagation();
                            await innerhivePlusDialogVM.requirePurchase({ triggerSource: 'recording ai processing' });
                        }}
                    >
                        Summarize with&nbsp;<div style={{marginTop: -1}}><InnerhivePlusText /></div>
                    </div>
                ) : undefined;
                return innerhivePlusMsg || (!recording.skipAIProcessing ? hours >= maxHours ? <ProcessingFailed /> : 'Processing...' : '');
            case 'summarized':
                return '';
            case 'transcribe-failed':
            case 'summarize-failed':
                return <ProcessingFailed />;
            default:
                return initCap(recording.status || 'unknown');
        }
    };

    const status = getRecordingStatus();
    return recording && (
        <div className={classes.recordingInfo} data-type="recording-info">
            <WaveformLinesIcon color="medium" />
            <span>{formatRecordingTime(recording.msDuration)}</span>
            {status && !hideStatus && <span>{status}</span>}
        </div>
    );
};
